/**
 * The terrain layer's bookkeeping, decided without a canvas (`projects/terrain-tile-art.md` 4.1–4.2):
 * which chunks a terrain or wall change dirties, which chunks are on screen, and how densely a
 * chunk rasterizes at a settled view. `terrainLayer.ts` owns the textures; this module only plans.
 */
import type { EdgeMap, TerrainMap } from "@lancer-vtt/domain";
import { CELL, parseEdgeCells, parseTileKey } from "../combatCells";
import { CHUNK, type Board, type CellRect, chunkRect } from "./geometry";

/**
 * How far, in cells, a changed terrain cell reaches into painted pixels: the widest field blur (the
 * sunken terraces, 3 cells) plus the longest shadow cast across a chunk edge (1 cell). Props and
 * walls reach less (`CHUNK_HALO`). `chunks.test.ts` pins it against the geometry.
 */
export const DIRTY_REACH = 4;

/** How far a changed edge wall reaches: its stroke, shadow and node stay within a cell. */
export const EDGE_REACH = 1;

/**
 * Terrain rasterizes at 1× or 2× device pixels per board pixel. Above 2× a 16×16 chunk would pass
 * the 4096 px texture limit, so the layer caps there: past a settled density of 2 the ground is
 * magnified and goes soft (crisp-zoom finding (b); Decision owed 10 keeps the 512 px atlas).
 */
export const MAX_DENSITY = 2;

/** A settled view wanting more than this many device pixels per board pixel repaints its visible chunks at {@link MAX_DENSITY}. */
export const HI_DENSITY_ABOVE = 1.5;

/** The byte cap on 2× chunk textures; the least recently seen off-screen ones are evicted first. */
export const HI_TEXTURE_BUDGET = 64 * 1024 * 1024;

export type ChunkKey = `${number},${number}`;

export function chunkKey(cx: number, cy: number): ChunkKey {
  return `${cx},${cy}`;
}

export function chunkRectOf(board: Board, key: ChunkKey): CellRect {
  const [cx, cy] = key.split(",").map(Number) as [number, number];
  return chunkRect(board, cx, cy);
}

/** Every chunk of the board, row by row. */
export function allChunks(board: Board): ChunkKey[] {
  const out: ChunkKey[] = [];
  for (let cy = 0; cy * CHUNK < board.height; cy++) for (let cx = 0; cx * CHUNK < board.width; cx++) out.push(chunkKey(cx, cy));
  return out;
}

/** Add the chunks within `reach` cells of cell `(x, y)` to `into`. */
function chunksNear(board: Board, { x, y, reach }: { x: number; y: number; reach: number }, into: Set<ChunkKey>): void {
  const cx0 = Math.max(0, Math.floor((x - reach) / CHUNK));
  const cy0 = Math.max(0, Math.floor((y - reach) / CHUNK));
  const cx1 = Math.min(Math.ceil(board.width / CHUNK) - 1, Math.floor((x + reach) / CHUNK));
  const cy1 = Math.min(Math.ceil(board.height / CHUNK) - 1, Math.floor((y + reach) / CHUNK));
  for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) into.add(chunkKey(cx, cy));
}

function changedKeys<T>(prev: Record<string, T>, next: Record<string, T>): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(next)) {
    const was = prev[k];
    if (was !== v && JSON.stringify(was) !== JSON.stringify(v)) out.push(k);
  }
  for (const k of Object.keys(prev)) if (!(k in next)) out.push(k);
  return out;
}

export type TerrainState = { terrain: TerrainMap; edges: EdgeMap };

/** The chunks whose pixels can differ between two terrain states on the same board. */
export function dirtyChunks(board: Board, prev: TerrainState, next: TerrainState): Set<ChunkKey> {
  const out = new Set<ChunkKey>();
  for (const k of changedKeys(prev.terrain, next.terrain)) {
    const { x, y } = parseTileKey(k);
    chunksNear(board, { x, y, reach: DIRTY_REACH }, out);
  }
  for (const k of changedKeys(prev.edges, next.edges)) {
    for (const c of parseEdgeCells(k)) chunksNear(board, { x: c.x, y: c.y, reach: EDGE_REACH }, out);
  }
  return out;
}

/** A rectangle of the board in world px: what the screen shows. */
export type WorldRect = { x: number; y: number; w: number; h: number };

/** The chunks `view` overlaps, nearest the view's centre first. */
export function visibleChunks(board: Board, view: WorldRect): ChunkKey[] {
  const span = CHUNK * CELL;
  const cx0 = Math.max(0, Math.floor(view.x / span));
  const cy0 = Math.max(0, Math.floor(view.y / span));
  const cx1 = Math.min(Math.ceil(board.width / CHUNK) - 1, Math.floor((view.x + view.w) / span));
  const cy1 = Math.min(Math.ceil(board.height / CHUNK) - 1, Math.floor((view.y + view.h) / span));
  const mx = view.x + view.w / 2;
  const my = view.y + view.h / 2;
  const out: { key: ChunkKey; d: number }[] = [];
  for (let cy = cy0; cy <= cy1; cy++) {
    for (let cx = cx0; cx <= cx1; cx++) out.push({ key: chunkKey(cx, cy), d: Math.hypot((cx + 0.5) * span - mx, (cy + 0.5) * span - my) });
  }
  return out.sort((a, b) => a.d - b.d).map((c) => c.key);
}

/** The density a settled view wants its visible chunks painted at: 1 or {@link MAX_DENSITY}. */
export function chunkDensity(scale: number, dpr: number): number {
  return scale * dpr > HI_DENSITY_ABOVE ? MAX_DENSITY : 1;
}

/** A chunk texture's size in bytes (RGBA) at `density`. */
export function chunkBytes(rect: CellRect, density: number): number {
  return Math.ceil((rect.x1 - rect.x0) * CELL * density) * Math.ceil((rect.y1 - rect.y0) * CELL * density) * 4;
}

/**
 * Which 2× textures to drop so `add` more bytes fit under `budget`: off-screen ones only, least
 * recently seen first. `null` when even that cannot make room, so the caller stops upgrading.
 */
export function planEviction(held: { key: ChunkKey; bytes: number; seen: number; visible: boolean }[], add: number, budget: number): ChunkKey[] | null {
  let total = held.reduce((s, h) => s + h.bytes, 0) + add;
  if (total <= budget) return [];
  const out: ChunkKey[] = [];
  for (const h of held.filter((c) => !c.visible).sort((a, b) => a.seen - b.seen)) {
    out.push(h.key);
    total -= h.bytes;
    if (total <= budget) return out;
  }
  return null;
}
